import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from '@gatsbyjs/reach-router'
import { Dialog, DialogContentText } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Subscribe } from '@react-rxjs/core'
import React, { FC, useCallback, useMemo } from 'react'
import { firstValueFrom } from 'rxjs'

import {
  latestAccessTokenExpiration$,
  refreshAccessToken,
  useAccessTokenExpiration,
} from '../../../lib/auth/expiration'
import { usePageUrl } from '../../../lib/pages'
import * as Styled from './styled'

const padTime = (value: number) => value.toString().padStart(2, '0')

const SessionExpirationModalContent: FC = () => {
  const { loginWithRedirect } = useAuth0()
  const location = useLocation()
  const { navigate } = usePageUrl()
  const { status, expiresAt } = useAccessTokenExpiration()

  const isOpen = useMemo(() => status === 'expired' || status === 'expiringSoon', [status])
  const timeLeft = Math.floor((expiresAt - Date.now()) / 1000) // purposely avoiding useMemo
  const timer = useMemo(
    () => `${padTime(Math.floor(timeLeft / 60))}:${padTime(timeLeft % 60)}`,
    [timeLeft],
  )

  const continueBrowsing = useCallback(async () => {
    if (status !== 'expiringSoon') {
      return
    }
    if (typeof window === 'undefined') {
      return
    }
    return firstValueFrom(refreshAccessToken())
  }, [status])

  const resumeSession = useCallback(() => {
    if (status !== 'expired') {
      return
    }
    loginWithRedirect({
      appState: {
        returnTo: location ? `${location.pathname}${location.search}` : '',
      },
    }).catch(() => navigate('/login'))
  }, [location, status, navigate, loginWithRedirect])

  const onClose = useMemo(
    () => (status === 'expired' ? undefined : continueBrowsing),
    [status, continueBrowsing],
  )

  if (!isOpen) {
    return null
  }
  return (
    <Dialog
      aria-describedby="scroll-dialog-description"
      aria-labelledby="scroll-dialog-title"
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      scroll="paper"
      open
    >
      <Styled.CustomDialogContent>
        <DialogContentText component="section" id="scroll-dialog-description" tabIndex={-1}>
          <Styled.TextContainer>
            <Styled.Title>
              {status === 'expiringSoon'
                ? 'Are you still here?'
                : status === 'expired'
                  ? 'Continue where you left off'
                  : null}
            </Styled.Title>
            <Styled.Text>
              {status === 'expiringSoon' ? (
                <>
                  Your logged session will be paused due to inactivity. You may resume it when
                  you&apos;re back.
                </>
              ) : status === 'expired' ? (
                <>
                  Your session was paused due to inactivity.
                  <br /> Ready to keep browsing?
                </>
              ) : null}
            </Styled.Text>
            {status === 'expiringSoon' && (
              <Styled.TimeoutText>Pausing in {timer}</Styled.TimeoutText>
            )}
          </Styled.TextContainer>
        </DialogContentText>
        {status !== 'expired' && (
          <Styled.CloseButton onClick={onClose}>
            <CloseIcon />
          </Styled.CloseButton>
        )}
      </Styled.CustomDialogContent>
      <Styled.CustomDialogActions>
        {status === 'expiringSoon' ? (
          <Styled.Button onClick={continueBrowsing}>Continue browsing</Styled.Button>
        ) : status === 'expired' ? (
          <Styled.Button onClick={resumeSession}>Resume session</Styled.Button>
        ) : null}
      </Styled.CustomDialogActions>
    </Dialog>
  )
}

export const SessionExpirationModal: FC = () => {
  // avoid SSR errors since Suspense not supported in this ReactDOMServer version
  // https://legacy.reactjs.org/docs/error-decoder.html/?invariant=294
  if (typeof window === 'undefined') {
    return null
  }
  return (
    <Subscribe source$={latestAccessTokenExpiration$} fallback={null}>
      <SessionExpirationModalContent />
    </Subscribe>
  )
}
